import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import FastClick from 'react-fastclick-alt';
import classNames from 'classnames/bind';
import styles from './CheckBox.css';
import { dt } from '../../../helpers/Utils/GetTailwindToken';

const cx = classNames.bind(styles);

/* istanbul ignore next */
class CheckBox extends PureComponent {
    /**
     * Supported React properties
     * @type {Object}
     * config.id is used to assign id to checkbox, *required field.
     * config.defaultChecked accepts boolean, if true is passed then by default it will be checked
     * config.value is used to assign value to checkbox
     * config.name is used to assign name to checkbox
     * refcallback is a callback function which gives the context of the component
     * disabled accepts boolean value, pass true to disable checkbox
     */

    static propTypes = {
        label: PropTypes.element,
        config: PropTypes.oneOfType([PropTypes.object, PropTypes.array]).isRequired,
        onClick: PropTypes.func,
        onChange: PropTypes.func,
        refCallBack: PropTypes.func,
        theme: PropTypes.string,
        labelClass: PropTypes.string,
        disabled: PropTypes.bool,
        automationId: PropTypes.string,
        enableFastClick: PropTypes.bool,
        labelContainerClass: PropTypes.string,
        checkboxNoOpacityLabelClass: PropTypes.string,
        labelclassName: PropTypes.string,
        size: PropTypes.oneOf(['Default', 'Sm', 'Lg']),
    };

    /** @properties {Default set up} */
    static defaultProps = {
        onClick: () => {},
        onChange: () => {},
        refCallBack: () => {},
        theme: null,
        label: null, // updated to handle this warning Failed prop type: The prop `label` is marked as required in `CheckBox`, but its value is `null`.
        labelClass: '',
        disabled: false,
        automationId: 'at-checkbox-default-id',
        enableFastClick: false,
        labelContainerClass: '',
        checkboxNoOpacityLabelClass: '', // Pass Prop value as 'noOpacityLabel' if opacity not required for Label of the checkbox
        labelclassName: '',
        size: 'Sm',
    };

    constructor() {
        //eslint-disable-line
        super();
    }

    onClick = (event) => {
        this.props.onClick(event);
    };

    onChange = (event) => {
        const eve = {
            value: event.target.value,
            event,
        };
        this.props.onChange(eve);
    };

    renderCheckbox() {
        const {
            label,
            refCallBack,
            labelClass,
            theme,
            config,
            disabled,
            automationId,
            labelContainerClass,
            checkboxNoOpacityLabelClass,
            labelclassName,
            size,
        } = this.props;
        const defaultProps = {
            type: 'checkbox',
            ref: refCallBack,
            className: styles.checkbox,
            onChange: this.onChange,
            onClick: this.onClick,
            ...config,
        };
        const baseLabelStyle = dt([
            'cursor-pointer',
            'relative',
            'text-black',
            'inline-block',
            'before:content-[""]',
            'before:absolute',
            'before:top-0',
            'before:left-0',
            'before:cursor-pointer',
            'before:border',
            'before:border-solid',
            'before:border-gray-60',
            'before:rounded-[3px]',
            'before:bg-white',
            'after:content-[""]',
            'after:cursor-pointer',
            'after:border',
            'after:border-solid',
            'after:absolute',
            'after:rotate-45',
            'after:border-transparent',
            'after:border-solid',
            'after:translate-x-1/2',
            'after:border-t-0',
            'after:!border-l-0',
        ]);
        const sizeLabelStyle =
            size === 'Sm'
                ? dt([
                      'pl-8',
                      'text-small',
                      'min-h-[20px]',
                      'before:w-5',
                      'before:h-5',
                      'after:top-[3px]',
                      'after:left-[3.5px]',
                      'after:w-[6.5px]',
                      'after:h-[10.5px]',
                      'after:border-r-[2.5px]',
                      'after:border-b-[2.5px]',
                  ])
                : dt([
                      'pl-9',
                      'text-normal',
                      'min-h-[24px]',
                      'before:w-6',
                      'before:h-6',
                      'after:top-1',
                      'after:left-[4px]',
                      'after:w-[8px]',
                      'after:h-[12.5px]',
                      'after:border-r-[3px]',
                      'after:border-b-[3px]',
                  ]);
        const labelStyle = cx(baseLabelStyle, sizeLabelStyle);
        const lineheightStyle = size !== 'Sm' ? dt(['leading-6']) : dt(['leading-5']);
        return (
            <span
                className={`${cx(theme, lineheightStyle)} ${dt(['inline-block', 'select-none'])}`}
                key={config.id}
            >
                {disabled ? (
                    <input
                        data-automation-id={`${automationId}-input-disabled`}
                        {...defaultProps}
                        disabled
                    />
                ) : (
                    <input data-automation-id={`${automationId}-input`} {...defaultProps} />
                )}
                <label
                    htmlFor={config.id}
                    className={`${labelStyle} ${labelclassName} ${cx(
                        labelClass,
                        'label',
                        checkboxNoOpacityLabelClass
                    )}`}
                    data-automation-id={`${automationId}-label`}
                >
                    <div
                        className={`${dt(['font-normal'])} ${cx(
                            labelContainerClass,
                            'labelContainer'
                        )}`}
                    >
                        {label}
                    </div>
                </label>
            </span>
        );
    }

    /**
     * Renders check box
     */
    render() {
        const { enableFastClick } = this.props;
        return enableFastClick ? (
            <FastClick> {this.renderCheckbox()} </FastClick>
        ) : (
            this.renderCheckbox()
        );
    }
}

export default CheckBox;
